import type { SaveGame } from "../types/player";
import { SAVE_VERSION } from "../types/player";
import { SKILL_IDS } from "../types";
import { createDefaultSaveGame } from "./defaultSave";
import { saveGameSchema } from "./saveSchema";

/**
 * Validates raw persisted data against the current save shape.
 * Returns null for anything that is not a valid current-version save.
 */
export function parseSaveGame(raw: unknown): SaveGame | null {
  const result = saveGameSchema.safeParse(raw);
  return result.success ? (result.data as SaveGame) : null;
}

type Migration = (state: unknown) => unknown;

const MIGRATIONS: Readonly<Record<number, Migration>> = {
  1: (state) => ({
    ...(state as Record<string, unknown>),
    saveVersion: 2,
    offers: { available: [], urgent: null },
  }),
  2: (state) => {
    const s = state as Record<string, unknown>;
    const offers = (s.offers as { available?: unknown } | undefined) ?? {};
    const available = Array.isArray(offers.available) ? offers.available : [];
    return {
      ...s,
      saveVersion: 3,
      offers: { available, urgent: null },
    };
  },
  3: (state) => ({
    ...(state as Record<string, unknown>),
    saveVersion: 4,
    lastSaveTime: 0,
  }),
  4: (state) => ({
    ...(state as Record<string, unknown>),
    saveVersion: 5,
    resultHistory: [],
  }),
  5: (state) => {
    const s = state as Record<string, unknown>;
    const history = Array.isArray(s.resultHistory) ? s.resultHistory : [];
    return {
      ...s,
      saveVersion: 6,
      resultHistory: history
        .filter(
          (entry): entry is Record<string, unknown> =>
            typeof entry === "object" && entry !== null,
        )
        .map((entry) => ({
          ...entry,
          taskScores: Array.isArray(entry.taskScores)
            ? entry.taskScores.filter(
                (n): n is number => typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 100,
              )
            : [],
        })),
    };
  },
  6: (state) => ({
    ...(state as Record<string, unknown>),
    saveVersion: 7,
    training: { bought: 0 },
  }),
  7: (state) => {
    const s = state as Record<string, unknown>;
    const player = (s.player as Record<string, unknown> | undefined) ?? {};
    const skills = (player.skills as Record<string, unknown> | undefined) ?? {};
    const backfilled = Object.fromEntries(
      SKILL_IDS.map((id) => [id, typeof skills[id] === "number" ? skills[id] : 0]),
    );
    return {
      ...s,
      saveVersion: 8,
      player: { ...player, skills: backfilled },
    };
  },
};

export function runMigrations(state: unknown, fromVersion: number): unknown {
  let result = state;
  for (let version = fromVersion; version < SAVE_VERSION; version += 1) {
    const migration = MIGRATIONS[version];
    if (migration) {
      result = migration(result);
    }
  }
  return result;
}

/**
 * Fail-safe migration entry point used by the persist middleware. Migrates raw
 * persisted data to the current version and validates it; returns the default
 * save for anything corrupt or unmigratable. Never throws.
 */
export function migrateSave(raw: unknown, version: number): SaveGame {
  if (typeof raw !== "object" || raw === null) {
    return createDefaultSaveGame();
  }
  try {
    const candidate = version === SAVE_VERSION ? raw : runMigrations(raw, version);
    return parseSaveGame(candidate) ?? createDefaultSaveGame();
  } catch {
    return createDefaultSaveGame();
  }
}
